// ─── OS Push Notification System (creator side only) ─────────────────────────
// Fires a real browser/OS notification — works on Android (Chrome) and on
// iPhone too, as long as the site has been added to the Home Screen (iOS
// 16.4+ requirement for any web push, since Yhuu isn't a native App Store app).
//
// Only creators ever call this. The anonymous receiver side never subscribes.
// Whether a push actually gets SENT (server-side) still depends on the
// creator having paid — see supabase/functions/send-push. Subscribing here
// just registers the device; it does not bypass the paywall.
import { supabase } from './supabase';

let _swRegistration: ServiceWorkerRegistration | null = null;

export async function registerSW(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  if (_swRegistration) return _swRegistration;
  try {
    _swRegistration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    return _swRegistration;
  } catch (e) {
    console.warn('SW registration failed:', e);
    return null;
  }
}

// Register the worker as soon as the creator dashboard/admin bundle loads.
// Permission is still requested only from the explicit Enable Push button.
if (typeof window !== 'undefined') {
  void registerSW();
}

export const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string;

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(char => char.charCodeAt(0)));
}

// Safe capability check — mirrors the iOS-safe guard pattern: referencing
// `Notification` directly on iOS Safari (not installed to Home Screen) throws.
export function isPushCapable(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

export function isIosNotInstalled(): boolean {
  const ua = navigator.userAgent || '';
  const isIos = /iphone|ipad|ipod/i.test(ua) && !(window as unknown as { MSStream?: unknown }).MSStream;
  const isStandalone =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true;
  return isIos && !isStandalone;
}

async function saveSubscription(profileId: string, subscription: PushSubscription) {
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.auth || !json.keys?.p256dh) {
    throw new Error('Browser subscription was missing endpoint/keys');
  }

  const { error } = await supabase.from('push_subscriptions').upsert(
    {
      profile_id: profileId,
      endpoint: json.endpoint,
      keys_auth: json.keys.auth,
      keys_p256dh: json.keys.p256dh,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'endpoint' }
  );
  if (error) {
    console.warn('Failed to save push subscription:', error);
    throw new Error(`DB save failed: ${error.message}`);
  }
}

// ── Request permission + subscribe. Must be called from a real user gesture
// (a button tap) — iOS/Android will otherwise ignore or silently no-op it. ───
// Returns 'granted' | 'denied' | 'unsupported' | 'error:<message>' — the
// 'error:' case means something threw (bad VAPID key, DB insert blocked by
// RLS, missing table, etc.) and the caller should surface the message
// instead of pretending it was a normal permission denial.
export async function enablePushForCreator(
  profileId: string
): Promise<'granted' | 'denied' | 'unsupported' | `error:${string}`> {
  if (!isPushCapable()) return 'unsupported';
  if (!VAPID_PUBLIC_KEY) return 'error:VITE_VAPID_PUBLIC_KEY is not set in this build';
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return 'denied';

    const reg = await registerSW();
    if (!reg) return 'error:Service worker failed to register (check /sw.js is deployed)';

    const existing = await reg.pushManager.getSubscription();
    if (existing) {
      await saveSubscription(profileId, existing);
      return 'granted';
    }
    const subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as unknown as BufferSource,
    });
    await saveSubscription(profileId, subscription);
    return 'granted';
  } catch (err) {
    console.warn('enablePushForCreator failed:', err);
    const message = err instanceof Error ? err.message : String(err);
    return `error:${message}`;
  }
}

// Quietly re-link an already-granted subscription (e.g. on every Dashboard
// load) — never prompts, so it's safe to call from a useEffect on mount.
export async function relinkPushIfGranted(profileId: string) {
  if (!isPushCapable() || !VAPID_PUBLIC_KEY) return;
  if (Notification.permission !== 'granted') return;
  try {
    const reg = await registerSW();
    if (!reg) return;
    const existing = await reg.pushManager.getSubscription();
    if (existing) { await saveSubscription(profileId, existing); return; }
    const subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as unknown as BufferSource,
    });
    await saveSubscription(profileId, subscription);
  } catch (err) {
    console.warn('relinkPushIfGranted failed:', err);
  }
}

export function hasPushPermission(): boolean {
  return isPushCapable() && Notification.permission === 'granted';
}
